import { createSelector } from 'reselect';
import { getMin, getMax, getHourIntervals, startOfHour, minutesElapsed, isAfter } from '../../lib/date.utils';
import { find, filter, forEach, sortBy, map, flatten, startsWith, get, uniq } from 'lodash';
import { filterSelectedPartnerSelector, stagesFilterSelector } from './filter';
import { notificationsSeenSelector } from '../authentication';

const timetable = state => state.events.timetable;
const notifications = state => state.events.timetable.notifications;

// TIMETABLE SELECTORS
export const daysSelector = createSelector(timetable, tt => tt.days);
export const nowSelector = createSelector(timetable, tt => tt.now);
export const allStagesSelector = createSelector(timetable, tt => tt.stages);
export const selectedDaySelector = createSelector(timetable, tt => tt.selectedDay);
export const zoomSelector = createSelector(timetable, tt => tt.zoom);

export const chatEnabledSelector = createSelector(timetable, tt => {
    return tt.chatEnabled;
});
export const notificationsSelector = createSelector(notifications, n => n || {});
export const showDotNotificationsSelector = createSelector(
    notificationsSelector,
    notificationsSeenSelector,
    (n, ns) => {
        const lastNotification = get(n, 'data[0]');
        return lastNotification && isAfter(lastNotification.createdAt, ns);
    }
);
export const stagesSelector = createSelector(timetable, stagesFilterSelector, (tt, stf) => {
    return filter(
        sortBy(
            tt.stages.filter(st => st.date === tt.selectedDay),
            s => parseInt(s.number)
        ),
        x => (stf.length ? stf.includes(x.name) : true)
    );
});

export const listViewEntriesSelector = createSelector(timetable, filterSelectedPartnerSelector, (tt, partnerId) => {
    return flatten(
        tt.stages.map(t =>
            (t.Performances || []).map(p => ({
                start: p.startHour,
                end: p.endHour,
                artistName: (p.Artist && p.Artist.name) || '',
                artistId: (p.Artist && p.Artist.id) || '',
                id: p.id,
                clearing: find(p.clearing, c => c.partnerId === partnerId),
                stageName: t.name,
                stageDate: t.date
            }))
        )
    );
});

export const listViewArtistsSelector = createSelector(listViewEntriesSelector, entries => {
    return map(uniq(sortBy(entries.map(x => x.artistName))), a => ({ value: a, label: a }));
});

export const listViewStagesSelector = createSelector(listViewEntriesSelector, entries => {
    return map(uniq(sortBy(entries.map(x => x.stageName))), s => ({ value: s, label: s }));
});

export const hoursSelector = createSelector(stagesSelector, stages => {
    return _hoursParser(stages);
});
export const entrySelector = createSelector(timetable, tt => (tt.entry.id ? tt.entry : null));
export const contactSelector = createSelector(timetable, tt => tt.entry.contact);
export const entryArtistSelector = createSelector(timetable, tt => tt.entry.artistId);
export const fetchingSelector = createSelector(timetable, tt => tt.fetching);

export const firstNowSelector = createSelector(timetable, tt => {
    const firstNow = find(sortBy(tt.stages, ['date']), stage => {
        const hoursParsed = _hoursParser([stage]);
        const elapsed = minutesElapsed(hoursParsed.min, hoursParsed.max, tt.now);
        return elapsed > 0;
    });
    return firstNow;
});

export const showNow = createSelector(firstNowSelector, nowStage => {
    return !!nowStage;
});

const _hoursParser = stages => {
    const dates = [];
    stages.forEach(s => {
        s.Performances.forEach(e => {
            dates.push(e.startHour, e.endHour);
        });
    });
    const min = startOfHour(getMin(dates));
    const max = startOfHour(getMax(dates));
    const intervals = getHourIntervals(min, max);
    return { intervals, min, max };
};
// DATA SELECTORS
export const getClearingForPartner = (entry, partner) => {
    if (get(entry, 'clearing.partnerId') === partner) {
        return entry.clearing;
    }
    return find(entry.clearing || [], c => c.partnerId === partner) || {};
};

export const showState = state => state && state !== 'hidden';

export const displayEntry = (artistName, clearing, artistsFilters, liveFilters, nonLinearFilters) => {
    let displayEntry = true;
    if (artistsFilters.length) {
        displayEntry = artistsFilters.includes(artistName);
    }
    if (liveFilters.length) {
        displayEntry = displayEntry && liveFilters.includes((clearing.live || '').toLowerCase());
    }
    if (nonLinearFilters.length) {
        displayEntry = displayEntry && nonLinearFilters.includes((clearing.nonLinear || '').toLowerCase());
    }
    return displayEntry;
};

export const nowOffset = (widthUnit, minHour, maxHour, now) => (widthUnit / 60) * minutesElapsed(minHour, maxHour, now);

export function stagesToState(stages) {
    const parsedStages = [];
    stages.forEach(s => {
        forEach(s.names, (name, date) => {
            parsedStages.push({
                ...s,
                name,
                date,
                Performances: s.Performances.filter(x => startsWith(x.date, date))
            });
        });
    });
    return parsedStages;
}



// WEBPACK FOOTER //
// ./src/selectors/events/timetable.js